import { Fragment, KeyboardEvent, useState } from 'react';
import { Box, Button, IconButton, InputBase, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Theme, alpha } from '@mui/material/styles';
import { Edit } from '@mui/icons-material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
    OrientationBandCategory,
    RangeDto,
    SteckbriefCalculationResultDto,
    TargetClassBasisDto,
} from '../ensan-steckbrief.dto';
import { isMetricSuppressed } from '../steckbrief-calculation-messages';
import { formatEuroRange } from '../steckbrief-investment';
import {
    CHARACTER_LIMIT_EXCEEDED_MSG,
    PROGNOSIS_BOUNDS,
    PROGNOSIS_METRICS,
    PROGNOSIS_REASON_MAX_LENGTH,
    PROGNOSIS_REASON_REQUIRED_MSG,
    PROGNOSIS_VALUE_NEGATIVE_MSG,
    PROGNOSIS_VALUE_RANGE_MSG,
    PRIORITY_TARGET_CLASS_OVERRIDE_KEY,
    PRIORITY_TARGET_CLASS_OVERRIDE_REASON_KEY,
    PrognosisBound,
    PrognosisMetric,
    TARGET_CLASS_OPTIONS,
    TARGET_CLASS_OVERRIDE_KEY,
    TARGET_CLASS_OVERRIDE_REASON_KEY,
    prognosisReasonKey,
    prognosisValueKey,
} from '../steckbrief-variant-fields';

/** Monospace figures, like the POC's numeric cells. */
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** The POC's small uppercase mono label (units, "bis", tags, editor label). */
const MICRO_LABEL = {
    fontFamily: MONO,
    fontSize: 9.5,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    flex: 'none',
} as const;

const TINY_BUTTON = { minWidth: 0, px: 1, py: '3px', fontSize: 12, textTransform: 'none' } as const;

/**
 * The "adjusted" colour of the POC (a violet, deliberately distinct from the
 * warning/error hues so an adjustment never reads as a problem), per mode.
 */
function editColors(theme: Theme): { ink: string; line: string } {
    return theme.palette.mode === 'dark' ? { ink: '#BCA2E6', line: '#493A63' } : { ink: '#6A4C93', line: '#C3AEE0' };
}

/** Total adjustable bounds (each metric × two bounds). */
const ADJUSTABLE_BOUND_COUNT = PROGNOSIS_METRICS.length * PROGNOSIS_BOUNDS.length;

interface SteckbriefPrognosisPanelProps {
    calc: SteckbriefCalculationResultDto;
    /** The form values — the adjusted bounds and their reasons live there (user-only form keys). */
    values: Record<string, unknown>;
    /** Inline save-time validation errors, keyed by form key. */
    errors: Record<string, string>;
    /** Writes a set of form values at once (the result is outdated until "(Re-)calculate"). */
    onApply: (patch: Record<string, unknown>) => void;
}

/** The two changeable Zielklassen: "Alle empfohlenen Maßnahmen" and "Nur Prio-Maßnahmen". */
type ClassTarget = 'all' | 'priority';

/** Form keys and test-id prefix of each changeable Zielklasse. */
const CLASS_TARGETS: Record<ClassTarget, { overrideKey: string; reasonKey: string; testId: string }> = {
    all: {
        overrideKey: TARGET_CLASS_OVERRIDE_KEY,
        reasonKey: TARGET_CLASS_OVERRIDE_REASON_KEY,
        testId: 'target-class',
    },
    priority: {
        overrideKey: PRIORITY_TARGET_CLASS_OVERRIDE_KEY,
        reasonKey: PRIORITY_TARGET_CLASS_OVERRIDE_REASON_KEY,
        testId: 'priority-target-class',
    },
};

/** The open Zielklasse editor: which class, the chosen stage (an `EfficiencyStage` name) and its reason. */
interface OpenClassEditor {
    target: ClassTarget;
    stage: string | null;
    reason: string;
    error: string | null;
}

/** Print label of a stage value (`GUT` → "Gut"); null for anything else. */
function stageLabel(value: unknown): string | null {
    return TARGET_CLASS_OPTIONS.find((option) => option.value === value)?.labelKey ?? null;
}

/** Stage value of a print label ("Gut" → `GUT`); null for anything else. */
function stageValue(label: string | null): string | null {
    return TARGET_CLASS_OPTIONS.find((option) => option.labelKey === label)?.value ?? null;
}

interface OpenEditor {
    metric: PrognosisMetric;
    bound: PrognosisBound;
    value: string;
    reason: string;
    error: string | null;
}

function formatValue(value: number | null | undefined, metric: PrognosisMetric): string {
    if (value === null || value === undefined) return 'k. A.';
    const digits = metric.unit === 't/a' ? 1 : 0;
    return `${value.toLocaleString('de-DE', { maximumFractionDigits: digits })} ${metric.unit}`;
}

function formatRange(range: RangeDto | null | undefined, metric: PrognosisMetric): string {
    if (!range) return '–';
    return `${formatValue(range.low, metric)} – ${formatValue(range.high, metric)}`;
}

/** Label of an orientation-band category code (the backend's language-agnostic `bandCategory`). */
function bandCategoryLabel(t: TFunction, category: OrientationBandCategory): string {
    switch (category) {
        case 'ADMINISTRATION':
            return t('Administrative buildings');
        case 'SCHOOL':
            return t('Schools');
        case 'SCHOOL_WITH_SPORTS_HALL':
            return t('Schools with sports hall');
        case 'KITA':
            return t('Day-care centres');
        case 'SPORTS_HALL':
            return t('Sports halls');
        case 'FIRE_STATION':
            return t('Fire stations');
        case 'COMMUNITY_CENTRE':
            return t('Community centres');
        case 'EVENT_VENUE':
            return t('Event venues');
        case 'INDOOR_POOL':
            return t('Indoor pools');
        default:
            // A category a newer backend added — the code beats an empty label.
            return String(category);
    }
}

/**
 * The displayed class limits (the BMWK doc's integer bands): Gut `< gutUpper`,
 * Solide `gutUpper – (maessigLower − 1)`, Mäßig `maessigLower – maessigUpper`,
 * Kritisch `> maessigUpper` — keyed by `EfficiencyStage` name.
 */
function bandTexts(bands: TargetClassBasisDto['bands']): Record<string, string> {
    return {
        GUT: `< ${formatFigure(bands.gutUpper)}`,
        SOLIDE: `${formatFigure(bands.gutUpper)} – ${formatFigure(bands.maessigLower - 1)}`,
        MAESSIG: `${formatFigure(bands.maessigLower)} – ${formatFigure(bands.maessigUpper)}`,
        KRITISCH: `> ${formatFigure(bands.maessigUpper)}`,
    };
}

/** A derivation figure, German format: at most one decimal, or exactly one with [fixed]. */
function formatFigure(value: number, fixed = false): string {
    return value.toLocaleString('de-DE', { minimumFractionDigits: fixed ? 1 : 0, maximumFractionDigits: 1 });
}

function numberOrNull(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Why a typed value was not accepted as a number. */
type ParseFailure = 'NOT_A_NUMBER' | 'AMBIGUOUS_SEPARATOR';

/**
 * Parses a typed number: digits with at most ONE decimal separator, comma or
 * dot. Thousands separators are never guessed — "60.000" (German thousands)
 * and "1,200" (English thousands) are ambiguous with a three-digit fraction,
 * so a separator followed by exactly three digits is rejected rather than
 * silently parsed as 60 or 1.2; anything else non-numeric is rejected too.
 */
function parseTyped(raw: string): number | ParseFailure {
    const match = /^(-?\d+)(?:[.,](\d+))?$/.exec(raw.trim());
    if (!match) return 'NOT_A_NUMBER';
    const [, integer, fraction] = match;
    if (fraction?.length === 3) return 'AMBIGUOUS_SEPARATOR';
    return Number(fraction === undefined ? integer : `${integer}.${fraction}`);
}

/**
 * Ersteinschätzung + Prognosewerte (GEN-782, POC "Vor-Analyse Prognosefreigabe"
 * without the lookalike comparison): on top the SYSTEM prognosis exactly as the
 * engine computed it — both print columns, never changed by adjustments, so it
 * stays traceable what was deviated from. Below, the six bounds of the "Alle
 * empfohlenen Maßnahmen" savings ranges are adjustable one by one, each with a
 * mandatory reason. Adjusted bounds are form values (null = system value): the
 * backend prints them, the reasons are stamped with the Variant but not printed.
 * The "Alle empfohlenen" Zielklasse is changeable the same way (a stage + a
 * mandatory reason, `targetClassOverride`) — printed in "Empfohlene
 * Sanierungsmaßnahmen"; the system table keeps the engine's class.
 *
 * Since GEN-788 all three ranges come from the calibrated prognosis model (the
 * cost saving is the sheet v6's H3 and may be negative, and so may its adjusted
 * bounds — see [PrognosisMetric.allowNegative]): a metric it deliberately does
 * not predict (a SUPPRESSED warning, e.g. CO₂ for Fernwärme, or the cost saving
 * of a carrier without a heat price) reads "not predicted" instead of "inputs
 * missing" — entering both bounds with a reason stays the way to print a range
 * (e.g. with the supplier's CO₂ factor); the Prio column's energy and CO₂
 * are "–" by design until the model runs per Prio subset (its cost range keeps
 * the former per-measure model).
 *
 * The Prio Zielklasse is the H1 point × the Prio set's energy share on the
 * orientation value (`priorityTargetClassSystem`) and changeable like the Alle
 * one (`priorityTargetClassOverride` + reason). "So entsteht die Zielklasse"
 * shows the inputs of both classes (`targetClassBasis`), read-only.
 */
export function SteckbriefPrognosisPanel({ calc, values, errors, onApply }: SteckbriefPrognosisPanelProps) {
    const { t, i18n } = useTranslation();
    const [editor, setEditor] = useState<OpenEditor | null>(null);
    const [classEditor, setClassEditor] = useState<OpenClassEditor | null>(null);

    /** The engine's Zielklasse — older backends without the system field report only the printed one. */
    const systemTargetClass = calc.targetClassSystem === undefined ? calc.targetClass : calc.targetClassSystem;
    /** The engine's Prio Zielklasse — older backends without the system field report only the printed one. */
    const systemPriorityTargetClass =
        calc.priorityTargetClassSystem === undefined
            ? (calc.priorityTargetClass ?? null)
            : calc.priorityTargetClassSystem;

    function systemClass(target: ClassTarget): string | null {
        return target === 'all' ? systemTargetClass : systemPriorityTargetClass;
    }

    /** The advisor's changed stage (print label) of a Zielklasse; null = the system class. */
    function changedClass(target: ClassTarget): string | null {
        return stageLabel(values[CLASS_TARGETS[target].overrideKey]);
    }

    /** The engine's range — advisor-only metrics have none; older backends report only the printed one. */
    function systemRange(metric: PrognosisMetric): RangeDto | null {
        if (metric.advisorOnly) return null;
        const system = calc[metric.systemKey];
        return (system === undefined ? calc[metric.printedKey] : system) ?? null;
    }

    function adjusted(metric: PrognosisMetric, bound: PrognosisBound): number | null {
        return numberOrNull(values[prognosisValueKey(metric, bound)]);
    }

    function effective(metric: PrognosisMetric, bound: PrognosisBound): number | null {
        return adjusted(metric, bound) ?? systemRange(metric)?.[bound] ?? null;
    }

    const adjustedCount = PROGNOSIS_METRICS.reduce(
        (count, metric) => count + PROGNOSIS_BOUNDS.filter((bound) => adjusted(metric, bound) !== null).length,
        0,
    );

    function openEditor(metric: PrognosisMetric, bound: PrognosisBound) {
        setClassEditor(null);
        const current = effective(metric, bound);
        const reason = values[prognosisReasonKey(metric, bound)];
        setEditor({
            metric,
            bound,
            // German decimal comma, like the displayed value (parseTyped accepts both).
            value: current === null ? '' : String(current).replace('.', ','),
            reason: typeof reason === 'string' ? reason : '',
            error: null,
        });
    }

    function clearBound(metric: PrognosisMetric, bound: PrognosisBound) {
        onApply({ [prognosisValueKey(metric, bound)]: null, [prognosisReasonKey(metric, bound)]: null });
    }

    /**
     * The reasons accept any length (QA feedback round — no native clipping);
     * one over the limit is not applied, with the character-limit error.
     */
    function reasonTooLong(reason: string): string {
        return t(CHARACTER_LIMIT_EXCEEDED_MSG, { length: reason.trim().length, max: PROGNOSIS_REASON_MAX_LENGTH });
    }

    function handleSave() {
        if (!editor) return;
        const { metric, bound } = editor;
        const next = parseTyped(editor.value);
        const fail = (error: string) => setEditor({ ...editor, error });
        if (next === 'NOT_A_NUMBER') return fail(t('Please enter a number.'));
        if (next === 'AMBIGUOUS_SEPARATOR') return fail(t('Please enter the number without thousands separators.'));
        const other = effective(metric, bound === 'low' ? 'high' : 'low');
        if (other !== null && (bound === 'low' ? next > other : next < other)) {
            return fail(t('The lower bound must not be above the upper bound.'));
        }
        const system = systemRange(metric)?.[bound] ?? null;
        if (next === system) {
            // Back at the system value — no adjustment to justify or store, and
            // never implausible (a negative H3 bound is the engine's own).
            clearBound(metric, bound);
            setEditor(null);
            return;
        }
        if (next < 0 && !metric.allowNegative) return fail(t(PROGNOSIS_VALUE_NEGATIVE_MSG));
        if (metric.max !== null && next > metric.max) return fail(t(PROGNOSIS_VALUE_RANGE_MSG));
        if (editor.reason.trim() === '') return fail(t(PROGNOSIS_REASON_REQUIRED_MSG));
        if (editor.reason.trim().length > PROGNOSIS_REASON_MAX_LENGTH) return fail(reasonTooLong(editor.reason));
        onApply({
            [prognosisValueKey(metric, bound)]: next,
            [prognosisReasonKey(metric, bound)]: editor.reason.trim(),
        });
        setEditor(null);
    }

    function handleEditorKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSave();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setEditor(null);
        }
    }

    function resetAll() {
        const patch: Record<string, unknown> = {};
        for (const metric of PROGNOSIS_METRICS) {
            for (const bound of PROGNOSIS_BOUNDS) {
                patch[prognosisValueKey(metric, bound)] = null;
                patch[prognosisReasonKey(metric, bound)] = null;
            }
        }
        setEditor(null);
        onApply(patch);
    }

    function openClassEditor(target: ClassTarget) {
        setEditor(null);
        const { overrideKey, reasonKey } = CLASS_TARGETS[target];
        const reason = values[reasonKey];
        setClassEditor({
            target,
            stage:
                typeof values[overrideKey] === 'string'
                    ? (values[overrideKey] as string)
                    : stageValue(systemClass(target)),
            reason: typeof reason === 'string' ? reason : '',
            error: null,
        });
    }

    function clearTargetClass(target: ClassTarget) {
        const { overrideKey, reasonKey } = CLASS_TARGETS[target];
        onApply({ [overrideKey]: null, [reasonKey]: null });
    }

    function handleClassSave() {
        if (!classEditor) return;
        const { target } = classEditor;
        const fail = (error: string) => setClassEditor({ ...classEditor, error });
        if (classEditor.stage === null) return fail(t('Please choose a class.'));
        if (stageLabel(classEditor.stage) === systemClass(target)) {
            // Back at the system class — nothing to justify or store.
            clearTargetClass(target);
            setClassEditor(null);
            return;
        }
        if (classEditor.reason.trim() === '') return fail(t(PROGNOSIS_REASON_REQUIRED_MSG));
        if (classEditor.reason.trim().length > PROGNOSIS_REASON_MAX_LENGTH) {
            return fail(reasonTooLong(classEditor.reason));
        }
        const { overrideKey, reasonKey } = CLASS_TARGETS[target];
        onApply({ [overrideKey]: classEditor.stage, [reasonKey]: classEditor.reason.trim() });
        setClassEditor(null);
    }

    function handleClassEditorKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleClassSave();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setClassEditor(null);
        }
    }

    function boundLabel(bound: PrognosisBound): string {
        return bound === 'low' ? t('Lower bound') : t('Upper bound');
    }

    function renderValueCell(metric: PrognosisMetric, bound: PrognosisBound) {
        const edited = adjusted(metric, bound) !== null;
        const system = systemRange(metric)?.[bound] ?? null;
        const label = `${boundLabel(bound)} ${t(metric.labelKey)}`;
        const button = (
            <IconButton
                size="small"
                onClick={() => openEditor(metric, bound)}
                title={t('Edit {{value}}', { value: label })}
                aria-label={t('Edit {{value}}', { value: label })}
                data-testid={`prognosis-edit-${metric.formKeyPrefix}-${bound}`}
                sx={{ p: 0.25, color: 'text.secondary', alignSelf: 'center' }}
            >
                <Edit sx={{ fontSize: 14 }} />
            </IconButton>
        );
        const valueText = (
            <Box
                component="span"
                sx={{
                    fontFamily: MONO,
                    fontSize: 15,
                    fontWeight: 600,
                    ...(edited && { color: (theme) => editColors(theme).ink }),
                }}
                data-testid={`prognosis-value-${metric.formKeyPrefix}-${bound}`}
            >
                {formatValue(effective(metric, bound), metric)}
            </Box>
        );
        const wasText = edited && (
            <Box
                component="span"
                sx={{ fontFamily: MONO, fontSize: 11.5, color: 'text.secondary', textDecoration: 'line-through' }}
                data-testid={`prognosis-was-${metric.formKeyPrefix}-${bound}`}
            >
                {formatValue(system, metric)}
            </Box>
        );
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '7px',
                    whiteSpace: 'nowrap',
                    justifyContent: bound === 'low' ? 'flex-start' : 'flex-end',
                }}
            >
                {bound === 'low' ? (
                    <>
                        {valueText}
                        {wasText}
                        {button}
                    </>
                ) : (
                    <>
                        {button}
                        {wasText}
                        {valueText}
                    </>
                )}
            </Box>
        );
    }

    /** The drawn "—— bis ——" connector between the two bounds (POC `.track`). */
    function renderTrack() {
        const rule = { flex: '1 1 auto', minWidth: 8, height: 0, borderTop: 1, borderColor: 'divider' };
        return (
            <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                <Box sx={rule} />
                <Box component="span" sx={{ ...MICRO_LABEL, color: 'text.secondary' }}>
                    {t('to')}
                </Box>
                <Box sx={rule} />
            </Box>
        );
    }

    /**
     * One reason line, placed under the number it changed (POC `.reason`): a
     * lower-bound reason starts under the lower value, an upper-bound reason
     * ends under the upper value.
     */
    function renderReason(metric: PrognosisMetric, bound: PrognosisBound, reason: string) {
        return (
            <Box
                key={bound}
                data-testid={`prognosis-reason-${metric.formKeyPrefix}-${bound}`}
                sx={{
                    gridColumn: { xs: '1 / -1', sm: '2 / -1' },
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                    justifyContent: bound === 'low' ? 'flex-start' : 'flex-end',
                    gap: '4px 9px',
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: 'text.secondary',
                    textAlign: bound === 'low' ? 'left' : 'right',
                }}
            >
                <Box
                    component="span"
                    sx={{
                        ...MICRO_LABEL,
                        fontSize: 9,
                        fontWeight: 600,
                        color: (theme) => editColors(theme).ink,
                        border: 1,
                        borderColor: (theme) => editColors(theme).line,
                        borderRadius: '3px',
                        px: '4px',
                    }}
                >
                    {bound === 'low' ? t('Lower bound changed') : t('Upper bound changed')}
                </Box>
                <Box component="em" sx={{ fontStyle: 'italic' }}>
                    {reason}
                </Box>
            </Box>
        );
    }

    function renderEditor(open: OpenEditor) {
        const edited = adjusted(open.metric, open.bound) !== null;
        const input = {
            fontSize: 13,
            bgcolor: 'background.default',
            border: 1,
            borderColor: 'divider',
            borderRadius: '6px',
            px: 1,
            py: 0.25,
            '&.Mui-focused': {
                outline: 2,
                outlineColor: 'primary.main',
                outlineOffset: 1,
                borderColor: 'primary.main',
            },
        };
        return (
            <Box
                data-testid="prognosis-editor"
                onKeyDown={handleEditorKeyDown}
                sx={{
                    gridColumn: '1 / -1',
                    my: '3px',
                    border: 1,
                    borderColor: 'primary.main',
                    borderRadius: '7px',
                    bgcolor: 'background.paper',
                    px: 1,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '6px',
                }}
            >
                <Box component="span" sx={{ ...MICRO_LABEL, fontWeight: 600, color: 'primary.main' }}>
                    {boundLabel(open.bound)}
                </Box>
                <InputBase
                    autoFocus
                    // Select the prefilled value so typing replaces it (POC behaviour).
                    onFocus={(e) => e.target.select()}
                    value={open.value}
                    onChange={(e) => setEditor({ ...open, value: e.target.value, error: null })}
                    inputProps={{
                        inputMode: 'decimal',
                        'aria-label': t('New value'),
                        'data-testid': 'prognosis-editor-value',
                    }}
                    sx={{ ...input, fontFamily: MONO, width: 94, flex: 'none' }}
                />
                <Box component="span" sx={{ fontFamily: MONO, fontSize: 11.5, color: 'text.secondary', ml: '-2px' }}>
                    {open.metric.unit}
                </Box>
                <InputBase
                    value={open.reason}
                    onChange={(e) => setEditor({ ...open, reason: e.target.value, error: null })}
                    placeholder={t('Reason (required, saved with the variant)')}
                    inputProps={{
                        'aria-label': t('Reason'),
                        'data-testid': 'prognosis-editor-reason',
                    }}
                    sx={{ ...input, flex: '1 1 200px' }}
                />
                {edited && (
                    <Button
                        size="small"
                        onClick={() => {
                            clearBound(open.metric, open.bound);
                            setEditor(null);
                        }}
                        data-testid="prognosis-editor-revert"
                        sx={TINY_BUTTON}
                    >
                        {t('Reset')}
                    </Button>
                )}
                <Button size="small" onClick={() => setEditor(null)} sx={TINY_BUTTON}>
                    {t('Cancel')}
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    data-testid="prognosis-editor-save"
                    sx={TINY_BUTTON}
                >
                    {t('Apply')}
                </Button>
                {open.error && (
                    <Box
                        role="alert"
                        sx={{ flex: '1 1 100%', fontSize: 12, color: 'error.main' }}
                        data-testid="prognosis-editor-error"
                    >
                        {open.error}
                    </Box>
                )}
            </Box>
        );
    }

    /**
     * "So entsteht die Zielklasse" — read-only transparency of both system
     * Zielklassen: the Ist orientation value, the class limits of the building's
     * category and each column's energy-saving point → renovated value → class.
     * Hidden without `targetClassBasis` (no orientation value, or an older backend).
     */
    function renderTargetClassBasis() {
        const basis = calc.targetClassBasis;
        if (!basis) return null;
        const orientation = formatFigure(basis.orientationValue);
        const bands = bandTexts(basis.bands);
        const unit = 'kWh/(m²a)';
        const label = { color: 'text.secondary' };
        const figures = { fontFamily: MONO, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' };
        const derivation = (
            testId: string,
            pointText: string | null,
            renovated: { point: number; value: number } | null,
            targetClass: string | null,
        ) => (
            <Box data-testid={testId} sx={figures}>
                {renovated === null ? (
                    t('N/A')
                ) : (
                    <>
                        <Box>{pointText}</Box>
                        <Box>
                            {`${orientation} × (1 − ${formatFigure(renovated.point)} %) = ${formatFigure(renovated.value, true)} ${unit} → `}
                            <Box component="strong" sx={{ fontWeight: 600 }}>
                                {targetClass ?? t('N/A')}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        );
        const allPoint = basis.all ? `${formatFigure(basis.all.energySavingPointPercent)} %` : null;
        const priority = basis.priority;
        const priorityPointText =
            priority &&
            `${t('Energy saving point')} ${allPoint ? `${allPoint} × ` : ''}${t('Priority share')} ${formatFigure(
                priority.energyShareOfTotalPercent,
            )} % = ${formatFigure(priority.energySavingPointPercent)} %`;
        return (
            <Box data-testid="target-class-basis">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('How the target class is derived')}
                </Typography>
                <Box
                    sx={{
                        mt: 0.75,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 290px) 1fr' },
                        columnGap: 2,
                        typography: 'body2',
                        '& > *': { py: 0.5, borderTop: { sm: 1 }, borderColor: { sm: 'divider' } },
                    }}
                >
                    <Box sx={label}>{t('Final energy consumption (current, heat + electricity / NGF)')}</Box>
                    <Box sx={{ ...figures, fontWeight: 600 }} data-testid="target-class-basis-orientation">
                        {`${orientation} ${unit}`}
                    </Box>

                    <Box sx={label} data-testid="target-class-basis-category">
                        {t('Class limits for {{category}}', { category: bandCategoryLabel(t, basis.bandCategory) })}
                    </Box>
                    <Box>
                        <Box sx={{ ...figures, display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
                            {TARGET_CLASS_OPTIONS.map((option) => (
                                <Box
                                    component="span"
                                    key={option.value}
                                    sx={{ whiteSpace: 'nowrap' }}
                                    data-testid={`target-class-basis-band-${option.value}`}
                                >
                                    {`${t(option.labelKey)} ${bands[option.value]}`}
                                </Box>
                            ))}
                        </Box>
                        {basis.defaultBands && (
                            <Typography
                                variant="caption"
                                sx={{ color: 'warning.main', display: 'block' }}
                                data-testid="target-class-basis-default-bands"
                            >
                                {t('Default values – building type missing or not mapped')}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={label}>{t('Complete renovation')}</Box>
                    {derivation(
                        'target-class-basis-all',
                        allPoint && `${t('Energy saving point')} ${allPoint}`,
                        basis.all && { point: basis.all.energySavingPointPercent, value: basis.all.renovatedValue },
                        systemTargetClass,
                    )}

                    <Box sx={label}>{t('Priority measures only')}</Box>
                    {derivation(
                        'target-class-basis-priority',
                        priorityPointText,
                        priority && { point: priority.energySavingPointPercent, value: priority.renovatedValue },
                        systemPriorityTargetClass,
                    )}
                </Box>
                <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}
                    data-testid="target-class-basis-rule"
                >
                    {t(
                        'The renovated value plus a {{percent}} % safety margin (× {{factor}}) decides the target class; it is never worse than the current class.',
                        {
                            percent: formatFigure((basis.upperLimitMargin - 1) * 100),
                            factor: basis.upperLimitMargin.toLocaleString('de-DE', { maximumFractionDigits: 2 }),
                        },
                    )}
                </Typography>
            </Box>
        );
    }

    /**
     * One changeable Zielklasse (POC row): the effective class, struck through
     * system class beside a changed one, the reason under the row and the
     * stage picker + mandatory reason editor.
     */
    function renderClassAdjustment(target: ClassTarget, title: string, description: string, changedLabel: string) {
        const { reasonKey, testId } = CLASS_TARGETS[target];
        const system = systemClass(target);
        const changed = changedClass(target);
        const reason = values[reasonKey];
        const open = classEditor?.target === target ? classEditor : null;
        const rowLabel = target === 'all' ? t('Target class') : t('Target class (priority measures only)');
        return (
            <Box data-testid={`${testId}-adjustment`}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                    {description}
                </Typography>
                <Box
                    sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '10px',
                        bgcolor: 'background.paper',
                        px: 2,
                        py: '9px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                        gap: '5px 14px',
                        ...(open && { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }),
                    }}
                >
                    <Box component="span" sx={{ fontSize: 13.5, fontWeight: 600, flex: '1 1 150px' }}>
                        {rowLabel}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '7px', whiteSpace: 'nowrap' }}>
                        {changed !== null && (
                            <Box
                                component="span"
                                sx={{
                                    fontFamily: MONO,
                                    fontSize: 11.5,
                                    color: 'text.secondary',
                                    textDecoration: 'line-through',
                                }}
                                data-testid={`${testId}-was`}
                            >
                                {system ?? t('N/A')}
                            </Box>
                        )}
                        <Box
                            component="span"
                            sx={{
                                fontFamily: MONO,
                                fontSize: 15,
                                fontWeight: 600,
                                ...(changed !== null && { color: (theme) => editColors(theme).ink }),
                            }}
                            data-testid={`${testId}-value`}
                        >
                            {changed ?? system ?? t('N/A')}
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => openClassEditor(target)}
                            title={t('Edit {{value}}', { value: title })}
                            aria-label={t('Edit {{value}}', { value: title })}
                            data-testid={`${testId}-edit`}
                            sx={{ p: 0.25, color: 'text.secondary', alignSelf: 'center' }}
                        >
                            <Edit sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Box>
                    {changed !== null && typeof reason === 'string' && reason && (
                        <Box
                            sx={{ flex: '1 1 100%', fontSize: 12, lineHeight: 1.45, color: 'text.secondary' }}
                            data-testid={`${testId}-reason`}
                        >
                            <Box
                                component="span"
                                sx={{
                                    ...MICRO_LABEL,
                                    fontSize: 9,
                                    fontWeight: 600,
                                    color: (theme) => editColors(theme).ink,
                                    border: 1,
                                    borderColor: (theme) => editColors(theme).line,
                                    borderRadius: '3px',
                                    px: '4px',
                                    mr: '9px',
                                }}
                            >
                                {changedLabel}
                            </Box>
                            <Box component="em" sx={{ fontStyle: 'italic' }}>
                                {reason}
                            </Box>
                        </Box>
                    )}
                    {errors[reasonKey] && (
                        <Box role="alert" sx={{ flex: '1 1 100%', fontSize: 12, color: 'error.main' }}>
                            {t(errors[reasonKey])}
                        </Box>
                    )}
                    {open && (
                        <Box
                            data-testid={`${testId}-editor`}
                            onKeyDown={handleClassEditorKeyDown}
                            sx={{
                                flex: '1 1 100%',
                                my: '3px',
                                border: 1,
                                borderColor: 'primary.main',
                                borderRadius: '7px',
                                bgcolor: 'background.paper',
                                px: 1,
                                py: 0.75,
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '6px',
                            }}
                        >
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={open.stage}
                                onChange={(_, stage: string | null) => {
                                    if (stage !== null) setClassEditor({ ...open, stage, error: null });
                                }}
                                aria-label={title}
                            >
                                {TARGET_CLASS_OPTIONS.map((option) => (
                                    <ToggleButton
                                        key={option.value}
                                        value={option.value}
                                        data-testid={`${testId}-option-${option.value}`}
                                        sx={{ ...TINY_BUTTON, px: 1.25 }}
                                    >
                                        {t(option.labelKey)}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            <InputBase
                                value={open.reason}
                                onChange={(e) => setClassEditor({ ...open, reason: e.target.value, error: null })}
                                placeholder={t('Reason (required, saved with the variant)')}
                                inputProps={{
                                    'aria-label': t('Reason'),
                                    'data-testid': `${testId}-editor-reason`,
                                }}
                                sx={{
                                    fontSize: 13,
                                    bgcolor: 'background.default',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: '6px',
                                    px: 1,
                                    py: 0.25,
                                    flex: '1 1 200px',
                                    '&.Mui-focused': {
                                        outline: 2,
                                        outlineColor: 'primary.main',
                                        outlineOffset: 1,
                                        borderColor: 'primary.main',
                                    },
                                }}
                            />
                            {changed !== null && (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        clearTargetClass(target);
                                        setClassEditor(null);
                                    }}
                                    data-testid={`${testId}-editor-revert`}
                                    sx={TINY_BUTTON}
                                >
                                    {t('Reset')}
                                </Button>
                            )}
                            <Button size="small" onClick={() => setClassEditor(null)} sx={TINY_BUTTON}>
                                {t('Cancel')}
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                onClick={handleClassSave}
                                data-testid={`${testId}-editor-save`}
                                sx={TINY_BUTTON}
                            >
                                {t('Apply')}
                            </Button>
                            {open.error && (
                                <Box
                                    role="alert"
                                    sx={{ flex: '1 1 100%', fontSize: 12, color: 'error.main' }}
                                    data-testid={`${testId}-editor-error`}
                                >
                                    {open.error}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    const prioRange: Record<string, RangeDto | null | undefined> = {
        energySavingPotential: calc.priorityEnergySavingPotentialRange,
        co2Saving: calc.priorityCo2SavingRange,
        costSaving: calc.priorityCostSavingRange,
    };
    const compareRows: { key: string; label: string; prio: string; all: string }[] = [
        {
            key: 'targetClass',
            label: t('Target class'),
            prio: systemPriorityTargetClass ?? '–',
            all: systemTargetClass ?? '–',
        },
        // Advisor-only metrics (Investitionskosten/m²) have no system value — they are not part
        // of the frozen Systemprognose, only of the adjustment section below.
        ...PROGNOSIS_METRICS.filter((metric) => !metric.advisorOnly).map((metric) => ({
            key: metric.formKeyPrefix,
            label: t(metric.labelKey),
            prio: formatRange(prioRange[metric.formKeyPrefix], metric),
            all: formatRange(systemRange(metric), metric),
        })),
    ];

    return (
        <Stack spacing={2} data-testid="prognosis-panel">
            {/* Systemprognose — frozen reference: what the engine computed, both print columns. */}
            <Box data-testid="prognosis-system-table">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('System prognosis')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                    {t('As calculated by the system. Adjustments below do not change these values.')}
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr 1fr', sm: '1.3fr 1fr 1fr' },
                        columnGap: 2,
                        typography: 'body2',
                        '& > *': { py: 0.5, borderTop: 1, borderColor: 'divider' },
                    }}
                >
                    <Box sx={{ typography: 'overline', lineHeight: 1.5, color: 'text.secondary' }}>{t('Metric')}</Box>
                    <Box sx={{ typography: 'overline', lineHeight: 1.5, color: 'text.secondary' }}>
                        {t('Priority measures only')}
                    </Box>
                    <Box sx={{ typography: 'overline', lineHeight: 1.5, color: 'text.secondary' }}>
                        {t('All recommended measures')}
                    </Box>
                    {compareRows.map((row) => (
                        <Fragment key={row.key}>
                            <Box>{row.label}</Box>
                            <Box
                                sx={{ fontVariantNumeric: 'tabular-nums' }}
                                data-testid={`prognosis-system-prio-${row.key}`}
                            >
                                {row.prio}
                            </Box>
                            <Box
                                sx={{ fontVariantNumeric: 'tabular-nums' }}
                                data-testid={`prognosis-system-all-${row.key}`}
                            >
                                {row.all}
                            </Box>
                        </Fragment>
                    ))}
                </Box>
                {/* GEN-788: the Prio column's energy and CO₂ (and, on older backends,
                    the target class) are null by design until the prognosis model
                    runs per Prio subset — say so, or "–" would read as an empty Prio set. */}
                {calc.priorityMeasures.length > 0 && calc.priorityEnergySavingPotentialRange == null && (
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}
                        data-testid="prognosis-prio-next-step"
                    >
                        {/* The Prio Zielklasse is computed since the point × Prio-share rule. */}
                        {systemPriorityTargetClass == null
                            ? t(
                                  'Priority measures only: the energy saving potential, CO₂ saving and target class follow in a next step (–).',
                              )
                            : t(
                                  'Priority measures only: the energy saving potential and CO₂ saving follow in a next step (–).',
                              )}
                    </Typography>
                )}
            </Box>

            {renderTargetClassBasis()}

            {/* Zielklasse anpassen — the printed target classes of both columns
                ("Alle empfohlenen" in Empfohlene Sanierungsmaßnahmen, "Nur
                Prio-Maßnahmen" in the comparison), each with a mandatory reason. */}
            {renderClassAdjustment(
                'all',
                t('Adjust target class'),
                t(
                    'The target class of "All recommended measures" printed in "Empfohlene Sanierungsmaßnahmen". A change needs a reason; it is saved with the variant (not printed).',
                ),
                t('Target class changed'),
            )}
            {renderClassAdjustment(
                'priority',
                t('Adjust target class (priority measures only)'),
                t(
                    'The target class of "Priority measures only" printed in the comparison. A change needs a reason; it is saved with the variant (not printed).',
                ),
                t('Target class (priority measures only) changed'),
            )}

            {/* Prognosewerte anpassen — the six adjustable bounds, each with a reason. */}
            <Box data-testid="prognosis-adjustments">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('Adjust forecast values')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                    {t(
                        'The three savings ranges of "All recommended measures" and the investment costs per m², each with a lower and an upper bound. Every adjustment needs a reason; it is saved with the variant (not printed).',
                    )}
                </Typography>
                <Box
                    sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '8px 16px',
                            px: 2,
                            py: '9px',
                            borderBottom: 1,
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            fontSize: 12.5,
                            color: 'text.secondary',
                        }}
                    >
                        <span data-testid="prognosis-status">
                            {adjustedCount === 0 ? (
                                t('No adjustment yet.')
                            ) : (
                                <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                    {t('{{count}} of {{total}} values adjusted', {
                                        count: adjustedCount,
                                        total: ADJUSTABLE_BOUND_COUNT,
                                    })}
                                </Box>
                            )}
                        </span>
                        {adjustedCount > 0 && (
                            <Button size="small" onClick={resetAll} data-testid="prognosis-reset-all" sx={TINY_BUTTON}>
                                {t('Discard all adjustments')}
                            </Button>
                        )}
                    </Box>
                    {PROGNOSIS_METRICS.map((metric) => {
                        const isOpen = editor?.metric.formKeyPrefix === metric.formKeyPrefix;
                        // Keyed by form key: two bounds can fail with the SAME
                        // message (e.g. both reasons missing).
                        const rowErrors = PROGNOSIS_BOUNDS.flatMap((bound) =>
                            [prognosisValueKey(metric, bound), prognosisReasonKey(metric, bound)]
                                .filter((key) => !!errors[key])
                                .map((key) => ({ key, message: errors[key] })),
                        );
                        return (
                            // One grid per metric row (POC `.mrow`): the divider spans the
                            // whole row, so label, bounds and connector share one baseline.
                            <Box
                                key={metric.formKeyPrefix}
                                data-testid={`prognosis-row-${metric.formKeyPrefix}`}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'auto minmax(20px, 1fr) auto',
                                        sm: 'minmax(150px, 290px) auto minmax(30px, 1fr) auto',
                                    },
                                    alignItems: 'center',
                                    columnGap: '14px',
                                    rowGap: '5px',
                                    px: 2,
                                    py: '9px',
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    '&:last-of-type': { borderBottom: 0 },
                                    ...(isOpen && {
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                    }),
                                }}
                            >
                                <Box
                                    sx={{
                                        gridColumn: { xs: '1 / -1', sm: 'auto' },
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        flexWrap: 'wrap',
                                        gap: '3px 7px',
                                        minWidth: 0,
                                    }}
                                >
                                    <Box component="span" sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>
                                        {t(metric.labelKey)}
                                    </Box>
                                    {/* Units keep their case — "T/A" would misread. */}
                                    <Box
                                        component="span"
                                        sx={{
                                            ...MICRO_LABEL,
                                            fontSize: 11,
                                            textTransform: 'none',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {metric.unit}
                                    </Box>
                                </Box>
                                {renderValueCell(metric, 'low')}
                                {renderTrack()}
                                {renderValueCell(metric, 'high')}
                                {systemRange(metric) === null &&
                                    !(effective(metric, 'low') !== null && effective(metric, 'high') !== null) && (
                                        <Box
                                            sx={{
                                                gridColumn: { xs: '1 / -1', sm: '2 / -1' },
                                                fontSize: 12,
                                                color: 'text.secondary',
                                            }}
                                            data-testid={`prognosis-null-hint-${metric.formKeyPrefix}`}
                                        >
                                            {/* Advisor-only: no system value by design (per-m², informed by
                                                lookalikes). Else GEN-788: a SUPPRESSED warning = not predicted. */}
                                            {metric.advisorOnly
                                                ? t(
                                                      'Advisor estimate — no system value. Enter both bounds with a reason to derive the total investment costs.',
                                                  )
                                                : isMetricSuppressed(calc.prognosisWarnings, metric.metric)
                                                  ? t(
                                                        'Not predicted for this building (see "Prognosis warnings") — enter both bounds with a reason to print a range.',
                                                    )
                                                  : t(
                                                        'No system value (inputs missing) — enter both bounds to print a range.',
                                                    )}
                                        </Box>
                                    )}
                                {metric.derivedTotalKey && calc[metric.derivedTotalKey] && (
                                    <Box
                                        sx={{
                                            gridColumn: { xs: '1 / -1', sm: '2 / -1' },
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'baseline',
                                            gap: '4px 9px',
                                            fontSize: 12.5,
                                            color: 'text.secondary',
                                        }}
                                        data-testid={`prognosis-derived-total-${metric.formKeyPrefix}`}
                                    >
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            {t('Total investment costs (derived)')}
                                        </Box>
                                        <Box
                                            component="span"
                                            sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {formatEuroRange(i18n.language, calc[metric.derivedTotalKey])}
                                        </Box>
                                        <Box component="span" sx={{ ...MICRO_LABEL, textTransform: 'none' }}>
                                            {t('= investment costs per m² × net floor area')}
                                        </Box>
                                    </Box>
                                )}
                                {PROGNOSIS_BOUNDS.map((bound) => {
                                    const reason = values[prognosisReasonKey(metric, bound)];
                                    return adjusted(metric, bound) !== null && typeof reason === 'string' && reason
                                        ? renderReason(metric, bound, reason)
                                        : null;
                                })}
                                {rowErrors.map(({ key, message }) => (
                                    <Box
                                        key={key}
                                        role="alert"
                                        sx={{
                                            gridColumn: { xs: '1 / -1', sm: '2 / -1' },
                                            fontSize: 12,
                                            color: 'error.main',
                                        }}
                                    >
                                        {t(message)}
                                    </Box>
                                ))}
                                {isOpen && editor && renderEditor(editor)}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Stack>
    );
}
